// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { CoilExternalLinks } from 'src/app/_interfaces/coil/coil-external-link-model';
import {
  GetCoilChannelHistory$Params,
  getCoilChannelHistory,
} from 'src/app/api/remote-api/.coil.generated/fn/coil-async/get-coil-channel-history';
import {
  GetCoilHistory$Params,
  getCoilHistory,
} from 'src/app/api/remote-api/.coil.generated/fn/coil-async/get-coil-history';
import { getCoilStatus } from 'src/app/api/remote-api/.coil.generated/fn/coil-async/get-coil-status';
import { getMarkers } from 'src/app/api/remote-api/.coil.generated/fn/coil-async/get-markers';
import { getSystemCoils } from 'src/app/api/remote-api/.coil.generated/fn/coil-async/get-system-coils';
import { ApiCoilChannelHistoryModel } from 'src/app/api/remote-api/.coil.generated/models/api-coil-channel-history-model';
import { ApiCoilChannelModel } from 'src/app/api/remote-api/.coil.generated/models/api-coil-channel-model';
import { MapApiService } from 'src/app/modules/shared/services/map-api.service';
import externalLinks from '../../../../app/config/externalLinks.json';
import { APICallState } from '../../shared/utils/api-call-state.util';

@Injectable({
  providedIn: 'root',
})
export class CoilService {
  //properties
  externalLinks: CoilExternalLinks[];

  constructor(private mapApiService: MapApiService) {
    this.externalLinks = externalLinks;
  }

  getCoilMarkers = this.mapApiService.mapEndpoint(getMarkers);
  getSystemCoils = this.mapApiService.mapEndpoint(getSystemCoils);
  getCoilStatus = this.mapApiService.mapEndpoint(getCoilStatus);

  /**
   * used to GENERATE a new set of lifecycle observables
   * for the coil history endpoint.
   * everytime this is invoked with a certain parameter set
   * a new endpoint is created and stored.
   * when called with the same paramter set the old endpoint
   * is lazily reused.
   * TODO: endpoints DO pile up over time. at some point
   * we might add logic that preempts the oldest endpoint
   * TODO: move this kind of logic to mapApiService.
   * We will likely need it in several places.
   * */
  private mappedCoilHistoryEndpoints = new Map<
    string,
    ReturnType<typeof this.mapCoilHistoryEndpoint>
  >();
  mapCoilHistoryEndpoint(params: {
    systemSerialNumber: string;
    systemMaterialNumber: number;
    coilMaterialNumber: string;
    coilSerialNumber: number;
  }): APICallState<
    GetCoilHistory$Params,
    ApiCoilChannelHistoryModel[],
    GetCoilHistory$Params
  > {
    const key = keyBySerialAndMaterialNumberAndCoil(params);
    if (this.mappedCoilHistoryEndpoints.has(key)) {
      return this.mappedCoilHistoryEndpoints.get(key)!;
    }
    const endpoint = this.mapApiService.mapEndpoint(getCoilHistory, {
      customCacheKey: keyBySerialAndMaterialNumberAndCoil,
    });
    this.mappedCoilHistoryEndpoints.set(key, endpoint);
    return endpoint;
  }

  private mappedCoilChannelHistoryEndpoints = new Map<
    string,
    ReturnType<typeof this.mapCoilChannelHistoryEndpoint>
  >();

  /**
   * see description of mapCoilHistoryEndpoint
   */
  mapCoilChannelHistoryEndpoint(params: {
    systemSerialNumber: string;
    systemMaterialNumber: number;
    coilMaterialNumber: string;
    coilSerialNumber: number;
  }): APICallState<
    GetCoilChannelHistory$Params,
    ApiCoilChannelModel[],
    GetCoilChannelHistory$Params
  > {
    const key = keyBySerialAndMaterialNumberAndCoil(params);
    if (this.mappedCoilChannelHistoryEndpoints.has(key)) {
      return this.mappedCoilChannelHistoryEndpoints.get(key)!;
    }
    const endpoint = this.mapApiService.mapEndpoint(getCoilChannelHistory, {
      customCacheKey: keyBySerialAndMaterialNumberAndCoil,
    });
    this.mappedCoilChannelHistoryEndpoints.set(key, endpoint);
    return endpoint;
  }

  // TODO - TP check with Jan. This is responsible for showing - Relevant information further available for this coil in the
  // coil information component
  getExternalLinks(): CoilExternalLinks[] {
    return this.externalLinks;
  }
}

/**
 * keys by system + coil
 * generates a unique key (aka string) given a param configuration
 * can be used to skip subsequent api calls for the exact same data!
 */
function keyBySerialAndMaterialNumberAndCoil(params: {
  systemSerialNumber: string;
  systemMaterialNumber: number;
  coilMaterialNumber: string;
  coilSerialNumber: number;
}) {
  return JSON.stringify(params);
}
